import { displayDate } from "@/lib/dates";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RegisterFeedback, PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, MapPin, Trash2, Edit3, ArrowRight, Users as UsersIcon, Calendar, Search, LayoutList, LayoutGrid, ListChecks, Receipt, Clock, DollarSign, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { StaticMapPreview, GetDirectionsLink } from "@/components/MapPreview";
import { JobBillingSection } from "@/components/JobBillingSection";

const JOB_GRID = "md:grid-cols-[minmax(180px,1.4fr)_minmax(140px,1fr)_88px_120px_88px] lg:grid-cols-[minmax(190px,1.4fr)_minmax(160px,1fr)_88px_120px_120px_88px]";

const JOB_PILL = {
  planned: "bg-[#233047]",
  in_progress: "bg-[#FF5F15]",
  on_hold: "bg-amber-500",
  completed: "bg-green-500",
};
const JOB_PILL_LABEL = {
  planned: "Planned",
  in_progress: "In Progress",
  on_hold: "On Hold",
  completed: "Completed",
};
// Subtle status pills for the list view (light + dark)
const LIST_PILL = {
  planned: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  in_progress: "bg-[#FF5F15]/10 text-[#FF5F15]",
  on_hold: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};
const LIST_DOT = {
  planned: "bg-blue-500",
  in_progress: "bg-[#FF5F15]",
  on_hold: "bg-amber-500",
  completed: "bg-emerald-500",
};

export default function Jobs() {
  const [mobileFilters, setMobileFilters] = useState(false);
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const navigate = useNavigate();
  const nav = navigate;
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [clientF, setClientF] = useState("all");
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState(() => localStorage.getItem("fl_jobs_view") || "list");
  const setViewPersist = (v) => { setView(v); localStorage.setItem("fl_jobs_view", v); };
  const [costs, setCosts] = useState(null);

  const load = async () => {
    setLoadError(false);
    setLoaded(false);
    try {
      const { data } = await api.get("/jobs");
      setItems(data);
      // If ?edit=ID was passed from JobDetail, open the edit dialog
      const editId = searchParams.get("edit");
      if (editId && isOffice) {
        const j = data.find((x) => x.id === editId);
        if (j) {
          setEditing(j);
          setOpen(true);
        }
        // clear query so refresh doesn't reopen
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    } catch (error) {
      setLoadError(true);
      console.warn("Register request failed", { status: error.response?.status });
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    if (isOffice) {
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
      api.get("/jobs/cost_summary").then((r) => setCosts(r.data.summaries || {})).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Job removed");
    load();
  };

  const counts = items.reduce((a, j) => { a[j.status] = (a[j.status] || 0) + 1; return a; }, {});
  const ql = q.trim().toLowerCase();
  const filtered = items
    .filter((j) => statusF === "all" || j.status === statusF)
    .filter((j) => !ql || [j.title, j.client_name, j.site_address, j.job_number].some((s) => (s || "").toLowerCase().includes(ql)))
    .sort((a, b) => {
      if (sort === "title") return (a.title || "").localeCompare(b.title || "");
      const ca = a.created_at || "", cb = b.created_at || "";
      return sort === "oldest" ? ca.localeCompare(cb) : cb.localeCompare(ca);
    });

  const STATS = [
    { key: "all", label: "All jobs", count: items.length, c: "#71717A", icon: ListChecks },
    { key: "in_progress", label: "Live", count: counts.in_progress || 0, c: "#FF5F15", icon: ListChecks },
    { key: "planned", label: "Upcoming", count: counts.planned || 0, c: "#3B82F6", icon: Calendar },
    { key: "on_hold", label: "On hold", count: counts.on_hold || 0, c: "#F59E0B", icon: Calendar },
    { key: "completed", label: "Completed", count: counts.completed || 0, c: "#22C55E", icon: ListChecks },
  ];

  const grouped = statusF === "all" && !ql && clientF === "all"
    ? [
        { key: "in_progress", label: "Live jobs" },
        { key: "planned", label: "Upcoming" },
        { key: "on_hold", label: "On hold" },
        { key: "completed", label: "Completed" },
      ].map((g) => ({ ...g, jobs: filtered.filter((j) => j.status === g.key) })).filter((g) => g.jobs.length > 0)
    : [{ key: "flat", label: null, jobs: filtered }];

  const openEdit = (j, ev) => { ev?.stopPropagation?.(); setEditing(j); setOpen(true); };

  return (
    <div data-testid="jobs-page" className="px-4 py-4 md:p-6 lg:p-6 w-full pb-24 lg:pb-10">
      <PageHeader
        tag="/ Active work"
        title={isOffice ? "Jobs" : "My jobs"}
        subtitle={isOffice ? "Manage active and upcoming work." : "Find your site, job details and work instructions."}
        action={isOffice && (
          <>
            <Button onClick={() => nav("/app/jobs/new")} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-job-button" data-tour-id="new-job-button">
              <Plus className="w-4 h-4 mr-2" /> New job
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              {editing && <JobForm key={editing.id} initial={editing} clients={clients} users={users} onDone={() => { setOpen(false); setEditing(null); load(); }} />}
            </Dialog>
          </>
        )}
      />

      {/* Summary stat tiles — double as status filters */}
      <div className="flex overflow-x-auto gap-2 pb-1 mb-3 md:grid md:grid-cols-5 md:overflow-visible" data-testid="jobs-filter-chips">
        {STATS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusF(s.key)}
            aria-pressed={statusF === s.key}
            className={`shrink-0 flex md:block items-center gap-2 text-left rounded-lg border px-3 py-2 md:py-2.5 transition-all bg-white dark:bg-[#101620] ${
              statusF === s.key
                ? "border-[#FF5F15] shadow-sm ring-1 ring-[#FF5F15]/30"
                : "border-zinc-200 dark:border-white/10 hover:border-zinc-400 dark:hover:border-white/25"
            }`}
            data-testid={`jobs-chip-${s.key}`}
          >
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.c }} />
              <span className="text-sm md:text-xl font-bold tabular-nums leading-none">{s.count}</span>
            </div>
            <div className="text-xs font-medium whitespace-nowrap md:mt-1.5" style={{ color: statusF === s.key ? "#FF5F15" : undefined }}>
              <span className={statusF === s.key ? "" : "text-zinc-500 dark:text-zinc-400"}>{s.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Toolbar: search + client filter + sort + view toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="jobs-toolbar">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search jobs"
            placeholder={isOffice ? "Search jobs, clients, addresses…" : "Search job name, number or site…"}
            className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
            data-testid="jobs-search-input"
          />
        </div>
        <Button variant="outline" className="md:hidden" aria-expanded={mobileFilters} onClick={() => setMobileFilters(v => !v)}>Filters{clientF !== 'all' ? ' · 1' : ''}</Button>
        <div className={`${mobileFilters ? 'flex' : 'hidden'} md:flex flex-wrap gap-2 w-full md:w-auto`}>
        {isOffice && clients.length > 0 && (
          <Select value={clientF} onValueChange={setClientF}>
            <SelectTrigger className="w-[160px] rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]" data-testid="jobs-client-select">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => (<SelectItem key={c.id} value={c.name || c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[150px] rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]" aria-label="Sort jobs" data-testid="jobs-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="title">Title A–Z</SelectItem>
          </SelectContent>
        </Select>
        </div>
        <div className={`${isOffice ? "hidden md:flex" : "hidden"} items-center rounded-lg border border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620] p-0.5 ml-auto`} data-testid="jobs-view-toggle">
          <button
            onClick={() => setViewPersist("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "list" ? "bg-[#111827] text-white dark:bg-[#FF5F15]" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
            data-testid="jobs-view-list">
            <LayoutList className="w-3.5 h-3.5" /> List
          </button>
          <button
            onClick={() => setViewPersist("cards")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "cards" ? "bg-[#111827] text-white dark:bg-[#FF5F15]" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
            data-testid="jobs-view-cards">
            <LayoutGrid className="w-3.5 h-3.5" /> Cards
          </button>
        </div>
      </div>

      {loadError ? <RegisterFeedback error onRetry={load} label="jobs" /> : !loaded ? (
        <div className="space-y-2" data-testid="jobs-loading">
          {[0, 1, 2, 3, 4].map((i) => (<div key={i} className="h-16 rounded-xl border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/5 animate-pulse" />))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No jobs found"
          description={items.length === 0 ? (isOffice ? "Create your first job." : "Your assigned jobs will appear here. Contact the office if you are expecting a job.") : "Try adjusting your filters or search."}
          action={isOffice && (
            <Button onClick={() => nav("/app/jobs/new")} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="empty-create-job">
              <Plus className="w-4 h-4 mr-2" /> Create job
            </Button>
          )}
        />
      ) : !isOffice ? (
        <div className="space-y-5" data-testid="field-jobs-list">
          {grouped.map(group => <section key={group.key}>
            {group.label && <h2 className="text-sm font-semibold mb-3">{group.label} <span className="text-zinc-500 dark:text-zinc-400 font-normal">· {group.jobs.length}</span></h2>}
            <div className="space-y-3">{group.jobs.map(job => <article key={job.id} className="field-job-card">
              <Link to={`/app/jobs/${encodeURIComponent(job.id)}`} className="block p-4" data-testid={`field-job-${job.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2"><span className="text-xs text-zinc-500 dark:text-zinc-400">{job.job_number || "Job"}</span><span className={`text-xs font-medium px-2 py-1 rounded-full ${LIST_PILL[job.status] || "bg-zinc-100 text-zinc-600"}`}>{JOB_PILL_LABEL[job.status] || job.status}</span></div>
                <h3 className="text-base font-semibold leading-snug break-words">{job.title}</h3>
                <p className="flex gap-2 text-sm text-zinc-500 dark:text-zinc-400 mt-2"><MapPin className="w-4 h-4 shrink-0 mt-0.5" /><span>{job.site_address || "Site address not provided"}</span></p>
              </Link>
              <div className="flex items-center justify-between gap-3 px-4 py-1 border-t border-zinc-200 dark:border-white/10">
                <Link to={`/app/jobs/${encodeURIComponent(job.id)}`} className="inline-flex items-center gap-2 min-h-11 text-sm font-semibold">View job <ArrowRight className="w-4 h-4" /></Link>
                <GetDirectionsLink lat={job.site_lat} lng={job.site_lng} address={job.site_address} className="min-h-11 !text-xs !normal-case !tracking-normal" testid={`field-job-directions-${job.id}`} />
              </div>
            </article>)}</div>
          </section>)}
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {filtered.map((j) => (
            <JobCard key={j.id} j={j} isOffice={isOffice} navigate={navigate} onEdit={openEdit} onDelete={remove} spend={costs ? costs[j.id] : undefined} />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="jobs-list-view">
          <div className={`hidden md:grid ${JOB_GRID} md:min-w-[760px] items-center gap-x-4 px-4 py-2 text-xs font-medium text-muted-foreground border-b border-border mb-2`} aria-hidden="true"><span>Job / customer</span><span>Site</span><span>Crew</span><span className="hidden lg:block">Dates</span><span>Status</span><span className="text-right">Actions</span></div>
          {grouped.map((g) => (
            <div key={g.key} className="md:min-w-[760px]">
              {g.label && (
                <div className="flex items-center gap-2.5 mt-3 mb-1.5 first:mt-0">
                  <span className={`w-2 h-2 rounded-full ${LIST_DOT[g.key] || "bg-zinc-400"}`} />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400">{g.label}</span>
                  <span className="text-xs font-mono text-zinc-400">{g.jobs.length}</span>
                  <span className="flex-1 h-px bg-zinc-200 dark:bg-white/[0.07]" />
                </div>
              )}
              <div className="space-y-1.5">
                {g.jobs.map((j) => (
                  <JobRow key={j.id} j={j} isOffice={isOffice} users={users} navigate={navigate} onEdit={openEdit} onDelete={remove} spend={costs ? costs[j.id] : undefined} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AvatarStack({ ids = [], users = [] }) {
  if (!ids.length) return <span className="text-[11px] text-zinc-400 font-mono">—</span>;
  const shown = ids.slice(0, 3);
  return (
    <span className="inline-flex items-center">
      <span className="flex -space-x-1.5">
        {shown.map((uid) => {
          const u = users.find((x) => x.id === uid);
          const initials = (u?.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
          return (
            <span key={uid} title={u?.name || uid}
                  className="w-6 h-6 rounded-full bg-[#FF5F15]/15 text-[#FF5F15] border border-white dark:border-[#101620] text-[11px] font-bold flex items-center justify-center">
              {initials}
            </span>
          );
        })}
      </span>
      {ids.length > 3 && <span className="ml-1 text-[11px] font-bold text-zinc-500">+{ids.length - 3}</span>}
    </span>
  );
}

function money(n) {
  return `$${(n || 0).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`;
}

function MarginBadge({ total, quoted, testId }) {
  if (!quoted) return null;
  const margin = Math.round(((quoted - (total || 0)) / quoted) * 100);
  const cls = margin < 0
    ? "bg-red-500/10 text-red-600 dark:text-red-400"
    : margin < 15
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
      : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums whitespace-nowrap ${cls}`}
          title="Live margin — quoted value minus cost so far" data-testid={testId}>
      {margin}% margin
    </span>
  );
}

function SpendCell({ spend, jobId }) {
  if (spend === undefined) return <span className="text-[11px] font-mono text-zinc-400">…</span>;
  const total = spend?.total || 0;
  const quoted = spend?.quoted_value;
  const over = quoted && total > quoted;
  const pct = quoted ? Math.min(100, Math.round((total / quoted) * 100)) : null;
  return (
    <div className="min-w-0">
      <div className={`flex items-center gap-1.5 text-[13px] font-extrabold tabular-nums leading-none ${over ? "text-red-600 dark:text-red-400" : ""}`}>
        <span>
          {money(total)}
          {quoted ? <span className="text-[11px] font-mono font-normal text-zinc-400"> / {money(quoted)}</span> : null}
        </span>
        <MarginBadge total={total} quoted={quoted} testId={jobId ? `job-margin-${jobId}` : undefined} />
      </div>
      {quoted ? (
        <div className="mt-1 h-1 rounded-full bg-zinc-100 dark:bg-white/10 overflow-hidden">
          <div className={`h-full rounded-full ${over ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">Spent</div>
      )}
    </div>
  );
}

function JobRow({ j, isOffice, users, navigate, onEdit, onDelete, spend }) {
  const pct = j.task_total > 0 ? Math.round((j.task_done / j.task_total) * 100) : null;
  const gridCls = JOB_GRID;
  const marginPct = isOffice && spend?.quoted_value ? Math.round(((spend.quoted_value - (spend.total || 0)) / spend.quoted_value) * 100) : null;
  const overMargin = isOffice && spend?.quoted_value && (spend?.total || 0) > spend.quoted_value;
  const thinMargin = isOffice && !overMargin && marginPct != null && marginPct < 10;
  return (
    <div
      onClick={(event) => {
        if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea, label, [role="button"], [role="link"], [role="checkbox"], [role="menuitem"], [contenteditable="true"]')) return;
        if (window.getSelection()?.toString()) return;
        navigate(`/app/jobs/${encodeURIComponent(j.id)}`);
      }}
      className={`group relative grid grid-cols-[minmax(0,1fr)_auto] ${gridCls} items-center gap-x-4 gap-y-1.5 px-4 py-3 md:py-1.5 md:min-h-[62px] rounded-lg border bg-white dark:bg-[#101620] hover:bg-zinc-50 dark:hover:bg-[#151e2b] transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-[#FF5F15] ${overMargin ? "!border-red-400 dark:!border-red-500/60 border-l-4 !border-l-red-500 bg-red-50/60 dark:bg-red-500/[0.07]" : "border-zinc-200 dark:border-white/10 hover:border-zinc-300 dark:hover:border-slate-600"}`}
      data-testid={`job-row-${j.id}`}
    >
      {/* Identity */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0 md:leading-3">
          {j.job_number && (
            <span className="font-mono font-bold text-[11px] md:text-[10px] md:leading-3 tracking-widest text-[#FF5F15] shrink-0" data-testid={`job-row-number-${j.id}`}>{j.job_number}</span>
          )}
          {overMargin && (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 shrink-0" data-testid={`job-over-margin-${j.id}`}>
              <AlertTriangle className="w-3 h-3" /> Over margin
            </span>
          )}
          {thinMargin && (
            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0" data-testid={`job-thin-margin-${j.id}`}>
              Thin margin
            </span>
          )}
          <span className={`md:hidden inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${LIST_PILL[j.status] || "bg-zinc-500/10 text-zinc-500"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${LIST_DOT[j.status] || "bg-zinc-400"}`} />
            {JOB_PILL_LABEL[j.status] || j.status}
          </span>
        </div>
        <Link to={`/app/jobs/${encodeURIComponent(j.id)}`} title={j.title} className="block font-semibold text-base md:text-[14px] leading-snug md:leading-[18px] line-clamp-2 md:line-clamp-1 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]" data-testid={`job-row-title-${j.id}`}>{j.title}</Link>
        {j.client_name && <div className="text-[11.5px] md:leading-[15px] text-zinc-500 dark:text-zinc-400 truncate">{j.client_name}</div>}
      </div>

      {/* Location (md+) */}
      <div className="hidden md:flex items-center gap-1.5 text-[12px] text-zinc-600 dark:text-zinc-300 min-w-0">
        {j.site_address && <MapPin className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
        <span className="truncate" title={j.site_address || undefined}>{j.site_address || "—"}</span>
      </div>

      {/* Crew (md+) */}
      <div className="hidden md:flex items-center gap-1.5">
        {!j.assigned_user_ids?.length ? <span className="text-xs text-zinc-400">—</span> : users.length > 0
          ? <AvatarStack ids={j.assigned_user_ids || []} users={users} />
          : <span className="inline-flex items-center gap-1.5 text-[12px] text-zinc-500"><UsersIcon className="w-3.5 h-3.5" />{j.assigned_user_ids?.length || 0}</span>}
      </div>

      {/* Dates (lg+) */}
      <div className="hidden lg:block text-[11px] font-mono text-zinc-500 dark:text-zinc-400 whitespace-nowrap leading-4">
        {j.start_date ? displayDate(j.start_date) : j.end_date ? displayDate(j.end_date) : "—"}{j.start_date && j.end_date && <span className="block">to {displayDate(j.end_date)}</span>}
      </div>

      {/* Status pill (md+) */}
      <div className="hidden md:block">
        <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${LIST_PILL[j.status] || "bg-zinc-500/10 text-zinc-500"}`}
              data-testid={`job-row-status-${j.id}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${LIST_DOT[j.status] || "bg-zinc-400"}`} />
          {JOB_PILL_LABEL[j.status] || j.status}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 justify-end">
        {isOffice && (
          <span className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <Button size="icon" variant="ghost" className="h-11 w-11 md:h-8 md:w-8 rounded-lg text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
                    onClick={(e) => onEdit(j, e)} aria-label={`Edit ${j.title}`} data-testid={`edit-job-${j.id}`}>
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-11 w-11 md:h-8 md:w-8 rounded-lg text-zinc-500 hover:text-red-600"
                    onClick={(e) => onDelete(j.id, e)} aria-label={`Delete ${j.title}`} data-testid={`delete-job-${j.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </span>
        )}
        <ArrowRight className="w-4 h-4 text-[#FF5F15] opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />
      </div>

      {/* Mobile secondary line */}
      <div className="md:hidden col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-500">
        {j.site_address && <span className="inline-flex items-center gap-1 min-w-0"><MapPin className="w-3 h-3 shrink-0" /><span className="truncate max-w-[180px]">{j.site_address}</span></span>}
        <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" />{j.assigned_user_ids?.length || 0} crew</span>
        {isOffice && spend !== undefined && (spend?.hours || 0) > 0 && (
          <span className="inline-flex items-center gap-1 font-bold tabular-nums text-zinc-700 dark:text-zinc-300"><Clock className="w-3 h-3" />{(spend.hours).toLocaleString("en-NZ", { maximumFractionDigits: 1 })}h</span>
        )}
        {isOffice && spend !== undefined && (
          <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${spend?.quoted_value && (spend?.total || 0) > spend.quoted_value ? "text-red-600 dark:text-red-400" : "text-zinc-700 dark:text-zinc-300"}`}>
            {money(spend?.total)} spent
          </span>
        )}
        {isOffice && spend !== undefined && (
          <MarginBadge total={spend?.total} quoted={spend?.quoted_value} />
        )}
        {pct != null && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-16 h-1.5 rounded-full bg-zinc-100 dark:bg-white/10 overflow-hidden inline-block">
              <span className={`block h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-[#FF5F15]"}`} style={{ width: `${pct}%` }} />
            </span>
            <span className="font-bold">{pct}%</span>
          </span>
        )}
      </div>
    </div>
  );
}

function JobCard({ j, isOffice, navigate, onEdit, onDelete, spend }) {
  const overMargin = isOffice && spend?.quoted_value && (spend?.total || 0) > spend.quoted_value;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/app/jobs/${j.id}`)}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); navigate(`/app/jobs/${j.id}`); } }}
      className={`flex flex-col rounded-xl border bg-white dark:bg-[#101620] shadow-sm hover:shadow-md transition-all p-5 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF5F15] ${overMargin ? "!border-red-400 dark:!border-red-500/60 border-l-4 !border-l-red-500" : "border-zinc-200 dark:border-white/10 hover:border-[#FF5F15]/50"}`}
      data-testid={`job-card-${j.id}`}>
      {/* Number chip + status pill */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {j.job_number ? (
            <span className="inline-block bg-[#FF5F15]/10 text-[#FF5F15] font-mono font-bold text-xs tracking-widest px-2.5 py-1 rounded-md"
                  data-testid={`job-card-number-${j.id}`}>
              {j.job_number}
            </span>
          ) : <span />}
          {overMargin && (
            <span className="inline-flex items-center gap-1 bg-red-500/15 text-red-600 dark:text-red-400 font-extrabold text-[10px] uppercase tracking-wider px-2 py-1 rounded-md" data-testid={`job-card-over-margin-${j.id}`}>
              <AlertTriangle className="w-2.5 h-2.5" /> Over margin
            </span>
          )}
          {j.billing_type === "charge_up" && (
            <span className="inline-flex items-center gap-1 bg-[#09090B] text-white font-bold text-[11px] uppercase tracking-widest px-2 py-1 rounded-md"
                  data-testid={`job-card-charge-up-${j.id}`}>
              <Receipt className="w-2.5 h-2.5 text-[#FF5F15]" /> Charge-Up
            </span>
          )}
        </div>
        <span className={`font-mono font-bold text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-md text-white ${JOB_PILL[j.status] || "bg-zinc-500"}`}
              data-testid={`job-card-status-${j.id}`}>
          {JOB_PILL_LABEL[j.status] || j.status}
        </span>
      </div>

      <h3 className="font-display font-bold uppercase tracking-tight text-lg leading-tight truncate"
          data-testid={`job-card-title-${j.id}`}>{j.title}</h3>
      {j.client_name && <div className="text-sm text-zinc-500 mt-0.5 truncate">{j.client_name}</div>}

      <div className="border-t border-zinc-100 dark:border-white/5 my-3" />

      {j.site_address && (
        <div className="flex items-start gap-2 text-[13px] text-zinc-600 dark:text-zinc-300 mb-2">
          <MapPin className="w-4 h-4 mt-0.5 text-zinc-400 shrink-0" />
          <span className="truncate flex-1">{j.site_address}</span>
        </div>
      )}

      {j.description && (
        <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">{j.description}</p>
      )}

      {j.task_total > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
            <span>Tasks</span>
            <span>{j.task_done}/{j.task_total}</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-100 dark:bg-white/10 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${j.task_done >= j.task_total ? "bg-green-500" : "bg-[#FF5F15]"}`}
              style={{ width: `${Math.round((j.task_done / j.task_total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {isOffice && spend !== undefined && ((spend?.hours || 0) > 0 || (spend?.total || 0) > 0 || spend?.quoted_value) && (
        <div className="mt-auto pt-3 grid grid-cols-2 gap-2" data-testid={`job-card-metrics-${j.id}`}>
          <div className="rounded-lg bg-zinc-50 dark:bg-white/5 px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400"><Clock className="w-3 h-3" /> Hours</div>
            <div className="text-base font-extrabold tabular-nums leading-tight mt-0.5" data-testid={`job-card-hours-${j.id}`}>{(spend?.hours || 0).toLocaleString("en-NZ", { maximumFractionDigits: 1 })}h</div>
          </div>
          <div className="rounded-lg bg-zinc-50 dark:bg-white/5 px-3 py-2">
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400"><DollarSign className="w-3 h-3" /> Cost</div>
            <div className={`text-base font-extrabold tabular-nums leading-tight mt-0.5 ${spend?.quoted_value && (spend?.total || 0) > spend.quoted_value ? "text-red-600 dark:text-red-400" : ""}`}>
              {money(spend?.total)}{spend?.quoted_value ? <span className="text-[10px] font-mono font-normal text-zinc-400"> / {money(spend.quoted_value)}</span> : null}
            </div>
          </div>
        </div>
      )}

      <div className="mt-auto pt-3 flex items-center justify-between gap-2 text-[11px] font-mono text-zinc-500 border-b border-zinc-100 dark:border-white/5 pb-3">
        <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {displayDate(j.start_date)} – {displayDate(j.end_date)}</span>
        <span className="inline-flex items-center gap-1.5"><UsersIcon className="w-3.5 h-3.5" /> {j.assigned_user_ids?.length || 0}</span>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FF5F15] group-hover:gap-2.5 transition-all"
              data-testid={`job-open-${j.id}`}>
          View Job <ArrowRight className="w-3.5 h-3.5" />
        </span>
        {isOffice && (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-lg border-zinc-200 dark:border-white/15"
                    onClick={(e) => onEdit(j, e)}
                    data-testid={`edit-job-card-${j.id}`}>
              <Edit3 className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-lg border-zinc-200 dark:border-white/15 text-zinc-500 hover:text-red-600 hover:border-red-300"
                    onClick={(e) => onDelete(j.id, e)}
                    data-testid={`delete-job-card-${j.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


export function JobForm({ initial, clients, users, onDone }) {
  const [form, setForm] = useState(initial || {
    title: "", client_id: "", site_address: "", site_lat: null, site_lng: null, description: "",
    status: "planned", start_date: "", end_date: "", assigned_user_ids: [],
    quoted_value: "", fleet_rate_overrides: {},
    billing_type: "fixed_price", charge_up_sources: { labour: true, plant: true, materials: true }, invoice_frequency: "manual",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const toggleAssigned = (uid) => {
    const a = form.assigned_user_ids || [];
    setForm({ ...form, assigned_user_ids: a.includes(uid) ? a.filter((x) => x !== uid) : [...a, uid] });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!initial && typeof form.bill_travel_time !== "boolean") { toast.error("Choose whether travel time is chargeable to the client"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        quoted_value: form.quoted_value === "" || form.quoted_value == null
          ? null
          : Number(form.quoted_value),
      };
      if (initial) {
        await api.patch(`/jobs/${initial.id}`, payload);
        toast.success("Job updated");
      } else {
        await api.post("/jobs", payload);
        toast.success("Job created");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const fieldUsers = users.filter((u) => u.role !== "admin");

  return (
    <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">{initial ? "Edit job" : "New job"}</DialogTitle>
        <DialogDescription>Link to a client, set status and assign the crew.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Title</Label>
          <Input required value={form.title} onChange={set("title")} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Henderson culvert renewal" data-testid="job-title-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Client</Label>
            <Select value={form.client_id} onValueChange={set("client_id")}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="job-client-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="job-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="on_hold">On hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="label-tag">Site address</Label>
          <div className="mt-2">
            <AddressAutocomplete
              value={form.site_address}
              onChange={(v) => setForm((f) => ({ ...f, site_address: v }))}
              onSelect={({ lat, lng }) => setForm((f) => ({ ...f, site_lat: lat ?? null, site_lng: lng ?? null }))}
              placeholder="123 Lincoln Rd, Auckland"
              className="rounded-sm border-zinc-400"
              testid="job-address-input"
            />
            {form.site_lat && form.site_lng && (
              <StaticMapPreview lat={form.site_lat} lng={form.site_lng} address={form.site_address} />
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Start date</Label>
            <Input type="date" value={form.start_date || ""} onChange={set("start_date")} className="mt-2 rounded-sm border-zinc-400" data-testid="job-start-input" />
          </div>
          <div>
            <Label className="label-tag">End date</Label>
            <Input type="date" value={form.end_date || ""} onChange={set("end_date")} className="mt-2 rounded-sm border-zinc-400" data-testid="job-end-input" />
          </div>
        </div>
        <div>
          <Label className="label-tag">Quoted value <span className="text-zinc-400 normal-case">— NZD, optional · used for live margin %</span></Label>
          <div className="mt-2 relative max-w-[220px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.quoted_value ?? ""}
              onChange={set("quoted_value")}
              placeholder="e.g. 12500"
              className="rounded-sm border-zinc-400 pl-7"
              data-testid="job-quoted-input"
            />
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
            <JobBillingSection value={form} onChange={(next) => setForm((f) => ({ ...f, ...next }))}
              isExistingJob={!!initial} originalBillingType={initial?.billing_type || "fixed_price"} />
          </div>
        </div>
        <div>
          <Label className="label-tag">Scope of works / description</Label>
          <Textarea
            rows={8}
            value={form.description}
            onChange={set("description")}
            className="mt-2 rounded-sm border-zinc-400 font-mono text-sm leading-relaxed"
            placeholder={"What needs doing on this job? E.g.\n\n• Excavate 18m trench along southern boundary\n• Install Ø450 RCRRJ culvert\n• Reinstate seal & line marking by Friday\n• Watch for fibre comms at 0.6m depth"}
            data-testid="job-desc-input"
          />
          <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            Bullet points or paragraphs · visible to the crew on their job details
          </div>
        </div>
        <div>
          <Label className="label-tag">Assign crew ({(form.assigned_user_ids || []).length})</Label>
          <div className="mt-2 max-h-40 overflow-y-auto border border-zinc-300 rounded-sm divide-y divide-zinc-200">
            {fieldUsers.length === 0 && <div className="p-3 text-xs text-zinc-500">No team members yet.</div>}
            {fieldUsers.map((u) => (
              <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-zinc-50 cursor-pointer text-sm">
                <input type="checkbox" checked={(form.assigned_user_ids || []).includes(u.id)} onChange={() => toggleAssigned(u.id)} data-testid={`assign-user-${u.id}`} />
                <span>{u.name}</span>
                <span className="text-xs text-zinc-500 ml-auto uppercase">{u.role}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="job-submit-button">
            {busy ? "Saving..." : initial ? "Save changes" : "Create job"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
